/**
 * Vocabulario do modulo de configuracoes num lugar so.
 *
 * Rotulos em `Record` com chave de uniao literal (nao index signature): se um
 * tipo compartilhado ganhar um valor novo (papel, status, provedor), o tsc
 * aponta o Record incompleto aqui em vez de renderizar `undefined` na tela.
 * Os nomes das cores de serie vem dos comentarios de index.css: a cor do logo
 * nunca e comunicada so pela amostra -- o nome acompanha (RNF16).
 *
 * Fontes das regras citadas: RF04/RF08 (SPEC.md); ADR-001 (janela de 7 dias,
 * ultimo contato, estorno por politica); docs/INTEGRACOES-WEBHOOK-GENERICO.md
 * (contrato canonico); docs/PLANO-INTEGRACOES-BR.md (status dos adaptadores e
 * ondas). Os textos de assinatura espelham src/ingest/adapters/*.ts do motor
 * -- o segredo NUNCA aparece aqui, so o esquema e o cabecalho.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { PROVIDER_LABEL } from '../../lib/format';
import type { CampaignChannel, CompanyStatus, FunnelKind, LogoTone, ProductOffer, UserRole } from '../../types';
import type {
  AbaConfiguracoes,
  RefundPolicy,
  VoiceName,
  WebhookEventStatus,
  WebhookEventType,
  WebhookProvider,
  WebhookRejectReason,
  Weekday,
} from './types';

/** Com acento, ao contrario de `CHANNEL_LABEL` em lib/format.ts (que e ASCII por heranca do backend). */
export const CANAL_LABEL: Readonly<Record<CampaignChannel, string>> = {
  voice: 'Ligação da IA',
  whatsapp: 'WhatsApp',
};

export const ABA_LABEL: Readonly<Record<AbaConfiguracoes, { readonly label: string; readonly icon: IconName }>> = {
  marca: { label: 'Marca', icon: 'empresa' },
  regua: { label: 'Régua', icon: 'relogio' },
  integracoes: { label: 'Integrações', icon: 'link' },
  equipe: { label: 'Equipe', icon: 'usuarios' },
  notificacoes: { label: 'Notificações', icon: 'alerta' },
};

export const ABAS: readonly AbaConfiguracoes[] = ['marca', 'regua', 'integracoes', 'equipe', 'notificacoes'];

export const STATUS_EMPRESA: Readonly<Record<CompanyStatus, { readonly label: string; readonly tone: BadgeTone }>> = {
  ativa: { label: 'Ativa', tone: 'good' },
  onboarding: { label: 'Em implantação', tone: 'warning' },
  pausada: { label: 'Pausada', tone: 'neutral' },
};

// -----------------------------------------------------------------------------
// Marca
// -----------------------------------------------------------------------------

export const TONES: readonly LogoTone[] = [1, 2, 3, 4, 5, 6, 7, 8];

export const TONE_NAME: Readonly<Record<LogoTone, string>> = {
  1: 'Azul',
  2: 'Laranja',
  3: 'Água',
  4: 'Amarelo',
  5: 'Magenta',
  6: 'Verde',
  7: 'Violeta',
  8: 'Vermelho',
};

export const TONE_CLASS: Readonly<Record<LogoTone, string>> = {
  1: 'bg-series-1',
  2: 'bg-series-2',
  3: 'bg-series-3',
  4: 'bg-series-4',
  5: 'bg-series-5',
  6: 'bg-series-6',
  7: 'bg-series-7',
  8: 'bg-series-8',
};

/** Mesma regra de `CompanyProvider`: primeira letra do primeiro e do ultimo nome. */
export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => p.length > 0);
  const primeira = partes[0]?.charAt(0) ?? 'C';
  const segunda = partes.length > 1 ? (partes[partes.length - 1]?.charAt(0) ?? '') : (partes[0]?.charAt(1) ?? '');
  return `${primeira}${segunda}`.toUpperCase();
}

// -----------------------------------------------------------------------------
// Regua
// -----------------------------------------------------------------------------

export const WEEKDAYS: readonly { readonly value: Weekday; readonly label: string; readonly short: string }[] = [
  { value: 'mon', label: 'Segunda-feira', short: 'Seg' },
  { value: 'tue', label: 'Terça-feira', short: 'Ter' },
  { value: 'wed', label: 'Quarta-feira', short: 'Qua' },
  { value: 'thu', label: 'Quinta-feira', short: 'Qui' },
  { value: 'fri', label: 'Sexta-feira', short: 'Sex' },
  { value: 'sat', label: 'Sábado', short: 'Sáb' },
  { value: 'sun', label: 'Domingo', short: 'Dom' },
];

export interface FunilInfo {
  readonly label: string;
  readonly icon: IconName;
  readonly route: string;
  readonly feed: string;
  /** Provedores que enviam o evento que alimenta o funil (status em docs/PLANO-INTEGRACOES-BR.md). */
  readonly providers: readonly WebhookProvider[];
}

export const FUNIL_INFO: Readonly<Record<FunnelKind, FunilInfo>> = {
  carrinho_abandonado: {
    label: 'Carrinho abandonado',
    icon: 'carrinho',
    route: '/funis/carrinho',
    feed: 'Alimentado pelo evento de checkout iniciado e não pago. Só entra na régua quem ficou 30 minutos sem pagar — antes disso a pessoa ainda está comprando.',
    providers: ['kiwify', 'hotmart', 'shopify', 'generic', 'hubla', 'eduzz', 'cakto', 'greenn'],
  },
  expirados: {
    label: 'Expirados',
    icon: 'calendario',
    route: '/funis/expirados',
    feed: 'Alimentado pelo evento de assinatura vencida ou cancelada. Só entra quem já deixou de ser assinante — ninguém é avisado antes do vencimento.',
    providers: ['kiwify', 'hotmart', 'generic', 'hubla', 'eduzz', 'cakto', 'greenn'],
  },
};

/** "T+30min", "T+1h", "T+24h" no carrinho; "D+15", "D+30" nos expirados. */
export function rotuloDoAtraso(kind: FunnelKind, minutes: number): string {
  if (kind === 'expirados') {
    return `D+${Math.max(1, Math.round(minutes / 1440))}`;
  }
  if (minutes < 60) {
    return `T+${minutes}min`;
  }
  if (minutes % 60 === 0) {
    return `T+${minutes / 60}h`;
  }
  return `T+${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;
}

/** "30 minutos depois do abandono", "2 dias depois do abandono", "15 dias depois do vencimento". */
export function descricaoDoAtraso(kind: FunnelKind, minutes: number): string {
  const marco = kind === 'expirados' ? 'do vencimento' : 'do abandono';
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const dias = minutes / 1440;
    return `${dias} ${dias === 1 ? 'dia' : 'dias'} depois ${marco}`;
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    const horas = minutes / 60;
    return `${horas} ${horas === 1 ? 'hora' : 'horas'} depois ${marco}`;
  }
  return `${minutes} minutos depois ${marco}`;
}

/**
 * O fato que abre cada funil e o atraso minimo do primeiro toque.
 *
 * Antes disto NAO existe toque, e isso e regra de negocio, nao de gosto: no
 * carrinho a pessoa so "abandonou" depois de 30 min sem pagar; nos expirados
 * so depois do vencimento. Um toque antes seria lembrete para quem ainda nao
 * saiu -- e nao ha recuperacao, nem comissao, sobre quem nao saiu. Espelha
 * `atrasoMinimoMinutos` do modulo de funis sem importar de la (os modulos
 * nascem em paralelo); quando o backend existir, os dois lerao a mesma regra.
 */
export interface FatoDoFunil {
  readonly minutos: number;
  readonly texto: string;
  /** Rotulo do marco zero na lista de toques ("Abandonou o checkout"). */
  readonly marcoZero: string;
  readonly marcoZeroRotulo: string;
  /** O que acontece com quem esta antes do fato. */
  readonly antesDoFato: string;
  /** Nome do fato em uma palavra, para frases ("do abandono"). */
  readonly nome: string;
}

export const FATO_DO_FUNIL: Readonly<Record<FunnelKind, FatoDoFunil>> = {
  carrinho_abandonado: {
    minutos: 30,
    texto: '30 minutos',
    marcoZero: 'Abandonou o checkout',
    marcoZeroRotulo: 'T+0',
    antesDoFato: 'Até 30 minutos sem pagar a pessoa ainda está comprando: nenhum toque sai. Quem paga nesse intervalo nunca é contatado e não gera comissão.',
    nome: 'abandono',
  },
  expirados: {
    minutos: 1440,
    texto: '1 dia',
    marcoZero: 'Plano venceu',
    marcoZeroRotulo: 'D+0',
    antesDoFato: 'Até o vencimento a pessoa ainda é assinante: não avisamos que o plano vai vencer. Quem renova em dia nunca entra nesta régua e não gera comissão.',
    nome: 'vencimento',
  },
};

export const NOTA_DEPOIS_DO_FATO =
  'Todo toque desta régua parte de um fato já ocorrido: o checkout ficou 30 minutos sem pagamento ou o plano venceu. Não existe lembrete antes disso — quem ainda está comprando ou ainda é assinante não saiu, e sem saída não há recuperação. A comissão do Converte Fácil existe justamente porque trazemos de volta quem já tinha ido.';

/** "12h", "12h30", "45min" a partir de minutos inteiros. */
export function formatarHoras(minutos: number): string {
  if (!Number.isFinite(minutos) || minutos <= 0) {
    return '0h';
  }
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) {
    return `${m}min`;
  }
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

/** "08:00" -> 480. `null` para texto que nao e HH:MM. */
export function minutosDoHorario(hhmm: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (m === null) {
    return null;
  }
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) {
    return null;
  }
  return h * 60 + min;
}

/**
 * Variacao entre duas contagens inteiras (atual x anterior), em pontos-base
 * inteiros. `relativaBps` e `null` quando o anterior e zero: "+300%" de nada
 * seria uma afirmacao falsa; a tela mostra o absoluto com "(antes: 0)".
 */
export interface Variacao {
  readonly direcao: 'alta' | 'queda' | 'igual' | 'sem_base';
  readonly relativaBps: number | null;
  readonly absoluta: number;
}

export function variar(atual: number, anterior: number | null): Variacao {
  if (anterior === null) {
    return { direcao: 'sem_base', relativaBps: null, absoluta: 0 };
  }
  const absoluta = atual - anterior;
  if (absoluta === 0) {
    return { direcao: 'igual', relativaBps: 0, absoluta: 0 };
  }
  const relativaBps = anterior === 0 ? null : Math.round((absoluta * 10_000) / anterior);
  return { direcao: absoluta > 0 ? 'alta' : 'queda', relativaBps, absoluta };
}

// -----------------------------------------------------------------------------
// Comissao e estorno (ADR-001)
// -----------------------------------------------------------------------------

export const REFUND_POLICY_INFO: Readonly<Record<RefundPolicy, { readonly label: string; readonly description: string }>> = {
  reverte_chargeback: {
    label: 'Chargeback reverte · reembolso não',
    description:
      'Padrão do produto. Reembolso não desfaz a comissão: o trabalho foi trazer o lead de volta; entrega e qualidade do produto são da empresa. Chargeback (a empresa nunca recebeu) desfaz.',
  },
  nao_reverte: {
    label: 'Nada reverte',
    description: 'Negociado em contrato: nem reembolso nem chargeback desfazem a comissão de uma venda recuperada.',
  },
  reverte_tudo: {
    label: 'Reembolso e chargeback revertem',
    description: 'Negociado em contrato: qualquer devolução do valor pago desfaz a comissão daquela venda.',
  },
};

// -----------------------------------------------------------------------------
// Integracoes
// -----------------------------------------------------------------------------

/** O rotulo unico do painel (`PROVIDER_LABEL`): um provedor novo entra num lugar so. */
export const WEBHOOK_PROVIDER_LABEL: Readonly<Record<WebhookProvider, string>> = PROVIDER_LABEL;

export interface WebhookProviderInfo {
  readonly label: string;
  /** Uma linha sob o titulo do cartao. */
  readonly descricao: string;
  /** Onde colar a URL (ou como chamar), para a dica do campo. */
  readonly hint: string;
  /** Esquema de assinatura, como o motor confere (src/ingest/adapters). */
  readonly esquema: string;
  readonly onde: string;
  readonly assinaCorpo: boolean;
  /** Como o provedor nomeia o evento de carrinho abandonado, para o teste. */
  readonly eventoDeTeste: string;
  /** Eventos que o provedor envia ao motor. */
  readonly eventos: readonly WebhookEventType[];
}

const TODOS_OS_EVENTOS: readonly WebhookEventType[] = ['cart_abandoned', 'payment_approved', 'payment_refunded', 'payment_chargeback', 'subscription_expired'];

export const WEBHOOK_PROVIDER_INFO: Readonly<Record<WebhookProvider, WebhookProviderInfo>> = {
  kiwify: {
    label: 'Kiwify',
    descricao: 'Adaptador dedicado, pronto no motor.',
    hint: 'Cole em Apps → Webhooks, marcando carrinho abandonado, compra aprovada, reembolso, chargeback e assinatura.',
    esquema: 'HMAC SHA-1 em hexadecimal (aceita SHA-256)',
    onde: 'cabeçalho x-kiwify-signature ou ?signature= na URL',
    assinaCorpo: true,
    eventoDeTeste: 'Evento de carrinho abandonado da Kiwify',
    eventos: TODOS_OS_EVENTOS,
  },
  hotmart: {
    label: 'Hotmart',
    descricao: 'Adaptador dedicado, pronto no motor.',
    hint: 'Cole em Ferramentas → Webhook (2.0), com os eventos de compra, carrinho abandonado, reembolso, chargeback e assinatura.',
    esquema: 'token compartilhado (hottok); o corpo não é assinado',
    onde: 'cabeçalho x-hotmart-hottok',
    assinaCorpo: false,
    eventoDeTeste: 'Evento de carrinho abandonado da Hotmart',
    eventos: TODOS_OS_EVENTOS,
  },
  shopify: {
    label: 'Shopify',
    descricao: 'Adaptador dedicado, pronto no motor. Não envia evento de assinatura.',
    hint: 'Cole em Configurações → Notificações → Webhooks, nos tópicos de checkout e de pedido (criado, pago, reembolsado, disputa).',
    esquema: 'HMAC SHA-256 em base64',
    onde: 'cabeçalhos x-shopify-hmac-sha256 e x-shopify-topic',
    assinaCorpo: true,
    eventoDeTeste: 'Tópico de checkout abandonado da Shopify',
    eventos: ['cart_abandoned', 'payment_approved', 'payment_refunded', 'payment_chargeback'],
  },
  generic: {
    label: 'Webhook genérico',
    descricao: 'Contrato canônico: checkout próprio, gateways, ERPs e automações (n8n, Make, Zapier).',
    hint: 'Faça um POST JSON neste endereço a partir do seu checkout, gateway, ERP ou automação, com o corpo do contrato abaixo.',
    esquema: 'HMAC SHA-256 em hexadecimal sobre o corpo cru',
    onde: 'cabeçalho x-cf-signature (aceita x-signature e o prefixo sha256=)',
    assinaCorpo: true,
    eventoDeTeste: 'event: cart_abandoned (ou o sinônimo carrinho_abandonado)',
    eventos: TODOS_OS_EVENTOS,
  },
  hubla: {
    label: 'Hubla',
    descricao: 'Adaptador dedicado, em beta até o primeiro evento real.',
    hint: 'Cole na regra de webhook da Hubla, com carrinho abandonado, fatura paga, reembolso, chargeback e assinatura desativada. Entregas de sandbox são descartadas.',
    esquema: 'token compartilhado; o corpo não é assinado',
    onde: 'cabeçalho x-hubla-token',
    assinaCorpo: false,
    eventoDeTeste: 'Evento lead.abandoned_checkout da Hubla',
    eventos: TODOS_OS_EVENTOS,
  },
  eduzz: {
    label: 'Eduzz',
    descricao: 'Adaptador dedicado da geração nova, em beta até o primeiro evento real. O legado Órbita/MyEduzz não é aceito.',
    hint: 'Cole no webhook da geração nova da Eduzz, com carrinho abandonado, fatura paga, reembolso, chargeback e contrato atrasado.',
    esquema: 'HMAC SHA-256 em hexadecimal ou base64',
    onde: 'cabeçalho x-signature (aceita o prefixo sha256=)',
    assinaCorpo: true,
    eventoDeTeste: 'Evento sun.cart_abandonment da Eduzz',
    eventos: TODOS_OS_EVENTOS,
  },
  cakto: {
    label: 'Cakto',
    descricao: 'Adaptador dedicado, em beta até o primeiro evento real. Recusa entrega antiga (fora da janela de tempo).',
    hint: 'Cole no webhook da Cakto, com checkout abandonado, compra aprovada, reembolso, chargeback e assinatura atrasada ou cancelada.',
    esquema: 'HMAC SHA-256 em hexadecimal sobre o instante e o corpo cru (prefixo v1=)',
    onde: 'cabeçalhos x-cakto-signature e x-cakto-timestamp',
    assinaCorpo: true,
    eventoDeTeste: 'Evento checkout_abandonment da Cakto',
    eventos: TODOS_OS_EVENTOS,
  },
  greenn: {
    label: 'Greenn',
    descricao: 'Adaptador dedicado da API pública do vendedor, em beta até o primeiro evento real.',
    hint: 'Cole no webhook da Greenn, com checkout abandonado, venda (paga, reembolsada, chargeback) e contrato.',
    esquema: 'token compartilhado; o corpo não é assinado',
    onde: 'cabeçalho x-webhook-token',
    assinaCorpo: false,
    eventoDeTeste: 'Evento checkoutAbandoned da Greenn',
    eventos: TODOS_OS_EVENTOS,
  },
};

/**
 * Plataformas que hoje entram pelo generico (docs/PLANO-INTEGRACOES-BR.md, ondas 1 a 5). Nomes de plataformas, nunca produtos de clientes.
 * A Eduzz saiu em 25/09/2026: a geracao nova tem adaptador proprio (onda 1), e o legado dela manda o token no corpo.
 */
export const PLATAFORMAS_PELO_GENERICO: readonly string[] = [
  'Monetizze',
  'Braip',
  'Perfect Pay',
  'Ticto',
  'Yampi',
  'Cartpanda',
  'Nuvemshop',
  'WooCommerce',
  'Mercado Pago',
  'Pagar.me',
  'Asaas',
  'Stripe',
  'PagBank',
  'Vindi',
  'n8n',
  'Make',
  'Zapier',
];

export const CONTRATO_GENERICO = {
  header: 'x-cf-signature',
  docs: 'docs/INTEGRACOES-WEBHOOK-GENERICO.md',
  plano: 'docs/PLANO-INTEGRACOES-BR.md',
  campos: [
    { campo: 'event', obrigatorio: 'sim', regra: 'um dos cinco eventos abaixo; sinônimos em português e inglês são aceitos' },
    { campo: 'order_id', obrigatorio: 'sim', regra: 'id do pedido no seu sistema; reentrega com o mesmo id e evento é descartada (idempotência)' },
    { campo: 'occurred_at', obrigatorio: 'não', regra: 'ISO-8601; sem ele vale o instante de chegada' },
    { campo: 'amount_cents ou amount', obrigatorio: 'pagamento e estorno', regra: 'centavos inteiros (preferido) ou decimal com ponto ("497.00")' },
    { campo: 'currency', obrigatorio: 'não', regra: 'padrão BRL' },
    { campo: 'customer.phone', obrigatorio: 'carrinho abandonado', regra: 'qualquer formato brasileiro; normalizado para E.164' },
    { campo: 'customer.name, customer.email', obrigatorio: 'não', regra: 'usados na mensagem e no painel' },
    { campo: 'product.*', obrigatorio: 'não', regra: 'checkout_url é para onde o botão “Finalizar compra” leva' },
    { campo: 'source', obrigatorio: 'não', regra: 'nome livre da plataforma de origem, só para o painel' },
  ],
  eventos: [
    { tipo: 'cart_abandoned', sinonimos: 'abandoned_cart, checkout_abandoned, carrinho_abandonado, abandoned', efeito: 'entra na fila de 30 min; depois ligação da IA e WhatsApp' },
    {
      tipo: 'payment_approved',
      sinonimos: 'paid, approved, purchase_approved, pagamento_aprovado, order_paid, pix_recebido',
      efeito: 'cancela a régua do lead e, se houve contato em até 7 dias, credita a conversão (ADR-001)',
    },
    { tipo: 'payment_refunded', sinonimos: 'refund, refunded, reembolso, estorno, order_refunded', efeito: 'registra o estorno; a comissão segue a política da empresa (padrão: reembolso não reverte)' },
    { tipo: 'payment_chargeback', sinonimos: 'chargeback, chargedback, disputa_perdida, dispute_lost', efeito: 'registra e, pela política padrão, reverte a comissão' },
    {
      tipo: 'subscription_expired',
      sinonimos: 'subscription_canceled, subscription_late, assinatura_vencida, assinatura_cancelada, expired',
      efeito: 'entra no funil de expirados (quando ligado para a empresa)',
    },
  ] as readonly { readonly tipo: WebhookEventType; readonly sinonimos: string; readonly efeito: string }[],
  respostas: [
    { codigo: 202, significado: 'carrinho abandonado aceito e agendado para T+30min' },
    { codigo: 200, significado: 'pagamento, estorno ou vencimento processado (ou reentrega já conhecida)' },
    { codigo: 400, significado: 'corpo inválido: o campo code diz o que corrigir (evento_desconhecido, telefone_invalido, valor_invalido...)' },
    { codigo: 401, significado: 'assinatura ausente ou inválida' },
    { codigo: 503, significado: 'integração sem segredo configurado no motor (a plataforma deve reentregar)' },
  ],
  testeRapido: (url: string): string =>
    [
      `BODY='{"event":"cart_abandoned","order_id":"teste-1","amount_cents":9900,"customer":{"phone":"+5511999900001","name":"Teste"}}'`,
      `SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$GENERIC_WEBHOOK_SECRET" | awk '{print $2}')`,
      `curl -s -X POST "${url}" -H "content-type: application/json" -H "x-cf-signature: $SIG" --data-binary "$BODY"`,
    ].join('\n'),
} as const;

/** Corpo de exemplo do contrato, montado com uma oferta da propria empresa (ficticia, do catalogo). */
export function exemploDeCorpoGenerico(oferta: ProductOffer, slug: string): string {
  const produtoId = oferta.id.replace(/^SIMULADO-/, '').toLowerCase();
  return [
    '{',
    '  "event": "cart_abandoned",',
    '  "order_id": "pedido-98421",',
    '  "occurred_at": "2026-09-24T09:03:11-03:00",',
    `  "amount_cents": ${oferta.priceCents},`,
    '  "currency": "BRL",',
    '  "customer": { "name": "Camila Duarte", "phone": "+55 11 99990-0001", "email": "camila@cliente.exemplo" },',
    `  "product": { "id": "${produtoId}", "name": ${JSON.stringify(oferta.name)}, "price_cents": ${oferta.priceCents}, "checkout_url": "https://checkout.${slug}.exemplo/c/98421" },`,
    '  "source": "checkout-proprio"',
    '}',
  ].join('\n');
}

export const EVENT_LABEL: Readonly<Record<WebhookEventType, string>> = {
  cart_abandoned: 'Carrinho abandonado',
  payment_approved: 'Pagamento aprovado',
  payment_refunded: 'Reembolso',
  payment_chargeback: 'Chargeback',
  subscription_expired: 'Assinatura vencida',
};

export const EVENT_STATUS: Readonly<Record<WebhookEventStatus, { readonly label: string; readonly tone: BadgeTone; readonly hint: string }>> = {
  accepted: { label: 'Aceito', tone: 'good', hint: 'Assinatura válida, evento reconhecido e gravado. Carrinho abandonado entra na fila de 30 min.' },
  duplicate: { label: 'Repetido', tone: 'neutral', hint: 'A plataforma reenviou um evento já gravado. O motor responde 200 e não faz nada de novo (idempotência).' },
  rejected: { label: 'Rejeitado', tone: 'critical', hint: 'O evento não entrou. O motivo diz o que corrigir.' },
};

export const REJECT_REASON_LABEL: Readonly<Record<WebhookRejectReason, { readonly titulo: string; readonly oQueFazer: string }>> = {
  assinatura_invalida: {
    titulo: 'Assinatura inválida',
    oQueFazer: 'O segredo da plataforma não bate com o configurado no motor (ou não foi configurado). Configure o segredo e o próximo evento já entra.',
  },
  corpo_invalido: { titulo: 'Corpo não é JSON válido', oQueFazer: 'Confira o que a plataforma envia: o motor só lê JSON.' },
  evento_desconhecido: { titulo: 'Evento que o motor não usa', oQueFazer: 'Ignorado de propósito. Só os cinco eventos do contrato entram.' },
  telefone_invalido: {
    titulo: 'Telefone ausente ou fora do padrão brasileiro',
    oQueFazer: 'Sem número não há ligação nem WhatsApp, então o carrinho não entra. Confira se o checkout exige o celular com DDD.',
  },
  valor_invalido: { titulo: 'Pagamento ou estorno sem valor', oQueFazer: 'A comissão depende do valor pago: envie amount_cents (centavos inteiros) ou amount.' },
};

/** Texto curto do codigo HTTP para a coluna do historico. */
export function descricaoHttp(status: number): string {
  switch (status) {
    case 202:
      return 'agendado';
    case 200:
      return 'ok';
    case 400:
      return 'dado inválido';
    case 401:
      return 'não autorizado';
    case 503:
      return 'sem segredo no motor';
    default:
      return String(status);
  }
}

export const VOICES: readonly { readonly value: VoiceName; readonly label: string }[] = [
  { value: 'ana', label: 'Ana — feminina, pt-BR' },
  { value: 'bruno', label: 'Bruno — masculina, pt-BR' },
  { value: 'clara', label: 'Clara — feminina, ritmo pausado, pt-BR' },
];

// -----------------------------------------------------------------------------
// Equipe
// -----------------------------------------------------------------------------

export const ROLE_INFO: Readonly<Record<UserRole, { readonly label: string; readonly tone: BadgeTone; readonly hint: string; readonly ordem: number }>> = {
  owner: { label: 'Dono', tone: 'brand', hint: 'Vê tudo, altera configurações e convida pessoas.', ordem: 0 },
  member: { label: 'Equipe', tone: 'neutral', hint: 'Vê funis, leads e contatos; não altera configurações.', ordem: 1 },
  comercial: { label: 'Comercial', tone: 'accent', hint: 'Vendedora: atende as respostas da régua na tela Comercial e registra venda assistida.', ordem: 2 },
  platform_admin: { label: 'Converte Fácil', tone: 'violet', hint: 'Equipe da plataforma; não é convidada por aqui.', ordem: 3 },
};

/** Papeis que o lojista pode atribuir num convite. `platform_admin` e nosso. */
export const ROLES_CONVIDAVEIS: readonly Exclude<UserRole, 'platform_admin'>[] = ['owner', 'member', 'comercial'];

// -----------------------------------------------------------------------------
// Validacao simples de formulario
// -----------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailValido(valor: string): boolean {
  return EMAIL_RE.test(valor.trim());
}

/** Aceita "https://loja.exemplo" e "loja.exemplo"; rejeita texto sem ponto. */
export function siteValido(valor: string): boolean {
  const texto = valor.trim();
  if (texto === '') {
    return true;
  }
  return /^(https?:\/\/)?[^\s/]+\.[^\s]+$/.test(texto);
}
